import {
  InteractionTarget,
  SlideCanvasBox,
  SlideCanvasPoint,
  SlideRenderBox,
  CanvasRect,
} from '../components/SlideCanvas.types';
import { isPointInBox, clamp } from './slideGeometry';
import { rotatePoint, ROTATION_HANDLE_RADIUS, getRotationHandleCenter } from './slideRotation';
import { ResizeHandle } from '../components/SlideCanvas.types';

/**
 * The radius used for hit detection of resize handles
 */
export const RESIZE_HANDLE_RADIUS = 10;

/**
 * Logic: Prioritized Hit Testing
 * 
 * Determines which part of the canvas the user is interacting with. 
 * We use a prioritized search to ensure small UI elements (handles) 
 * are detectable even when overlapping larger elements (box interiors).
 */

/**
 * Main orchestrator for hit detection.
 * Priority:
 * 1. Resize Handles of the CURRENTLY SELECTED box (Highest priority).
 * 2. Rotation Handle of the SELECTED box.
 * 3. Interior of any visible box (Top-most box first).
 * 4. Background (Lowest priority).
 */
export function findInteractionTarget(params: {
  pointNatural: SlideCanvasPoint;
  pointCanvas: SlideCanvasPoint;
  selectedBox: SlideRenderBox | null;
  allBoxes: SlideRenderBox[];
  scaleX: number;
  scaleY: number;
}): InteractionTarget {
  const { pointNatural, pointCanvas, selectedBox, allBoxes, scaleX, scaleY } = params;

  // 1. Check Resize Handles (Only for the selected box)
  if (selectedBox) {
    const handle = getResizeHandleHit(pointCanvas, selectedBox, scaleX, scaleY);
    if (handle) {
      return { type: 'resize-handle', box: selectedBox, handle };
    }

    // 2. Check Rotation Handle (Only for the selected box)
    if (isRotationHandleHit(pointCanvas, selectedBox, scaleX, scaleY)) {
      return { type: 'rotation-handle', box: selectedBox };
    }
  }

  // 3. Check Interiors (Iterate all boxes, backwards to respect stack order)
  for (let i = allBoxes.length - 1; i >= 0; i--) {
    const box = allBoxes[i];
    if (isPointInBox(pointNatural, box)) {
      return { type: 'interior', box };
    }
  }

  // 4. Fallback: Background
  return { type: 'background' };
}

/**
 * Determines if a pointer click has hit any of the resize handles of a given box.
 */
export function getResizeHandleHit(
  pointCanvas: SlideCanvasPoint,
  box: SlideRenderBox,
  scaleX: number,
  scaleY: number,
): ResizeHandle | null {
  const center = {
    x: (box.x + box.width / 2) * scaleX,
    y: (box.y + box.height / 2) * scaleY,
  };
  const width = box.width * scaleX;
  const height = box.height * scaleY;

  const corners: Record<string, ResizeHandle> = {
    NorthWest: 'NorthWest',
    NorthEast: 'NorthEast',
    SouthWest: 'SouthWest',
    SouthEast: 'SouthEast',
  };

  const localCorners = {
    NorthWest: { x: -width / 2, y: -height / 2 },
    NorthEast: { x: width / 2, y: -height / 2 },
    SouthWest: { x: -width / 2, y: height / 2 },
    SouthEast: { x: width / 2, y: height / 2 },
  };

  for (const [key, handle] of Object.entries(corners)) {
    const pos = rotatePoint(
      {
        x: center.x + localCorners[key as keyof typeof localCorners].x,
        y: center.y + localCorners[key as keyof typeof localCorners].y
      },
      center,
      box.rotation
    );
    const dx = pointCanvas.x - pos.x;
    const dy = pointCanvas.y - pos.y;
    if (dx * dx + dy * dy <= RESIZE_HANDLE_RADIUS * RESIZE_HANDLE_RADIUS) {
      return handle as ResizeHandle;
    }
  }

  return null;
}


/**
 * Checks if the pointer is within the circular hit area of the rotation handle.
 */
function isRotationHandleHit(
  pointCanvas: SlideCanvasPoint,
  box: SlideCanvasBox,
  scaleX: number,
  scaleY: number,
): boolean {
  const centerCanvas = {
    x: (box.x + box.width / 2) * scaleX,
    y: (box.y + box.height / 2) * scaleY,
  };
  const heightCanvas = box.height * scaleY;

  const handleCenter = getRotationHandleCenter(centerCanvas, heightCanvas, box.rotation);

  const dx = pointCanvas.x - handleCenter.x;
  const dy = pointCanvas.y - handleCenter.y;

  return (dx * dx + dy * dy) <= ROTATION_HANDLE_RADIUS * ROTATION_HANDLE_RADIUS;
}

/**
 * Logic: Pointer Translation
 * Concordat conversion from screen/client coordinates to local canvas space.
 */
export function getCanvasPointRaw(
  clientX: number,
  clientY: number,
  rect: CanvasRect,
  maxWidth: number,
  maxHeight: number,
): SlideCanvasPoint {
  const xCanvas = clamp(clientX - rect.left, 0, maxWidth);
  const yCanvas = clamp(clientY - rect.top, 0, maxHeight);
  return { x: xCanvas, y: yCanvas };
}

/**
 * Logic: Natural Coordinate Scaling
 * Converts a point from visual canvas pixels to the natural units of the underlying image.
 */
export function toNaturalPoint(
  pointCanvas: SlideCanvasPoint,
  scaleX: number,
  scaleY: number,
): SlideCanvasPoint {
  return {
    x: pointCanvas.x / scaleX,
    y: pointCanvas.y / scaleY,
  };
}
